import React, { useContext, useState } from 'react';

import Toggle from '@atlaskit/toggle';

import { Table } from '@qality/common-components/src/atoms/Table';
import { stopPropagation } from '@qality/common-components/src/lib/util/event';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { IS_CLOUD } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { StorageOfUniqueServices } from '@qality/common-service';
import { QAlitySettingsContext } from '@qality/common-state/src/QAlitySettingsProvider';

import { NoPermissionToAddIssueTypeModal } from './NoPermissionToAddIssueTypeModal';
import { ProjectSettingsRowProps } from './ProjectSettings.types';
import { RedirectToProjectSettingsConfirmationModal } from './RedirectToProjectSettingsConfirmationModal';
import { StatusOfQAlityIssueTypeSetup } from './StatusOfQAlityIssueTypeSetup';

const NO_PERMISSION_FAILED_CODE = 403;

export const ProjectSettingsRow: React.FC<ProjectSettingsRowProps> = ({
  project,
  rowIndex,
}: ProjectSettingsRowProps) => {
  const {
    toggleAppVisibilityInAllIssues,
    togglePluginInProject,
    addTestCaseIssueTypeToProjectScheme,
    removeTestCaseIssueTypeFromProjectScheme,
  } = useContext(QAlitySettingsContext);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);

  const toggleQalityTestIssueType = async () => {
    if (project.doesGivenProjectHaveQAlityTest()) {
      try {
        await removeTestCaseIssueTypeFromProjectScheme(project.id);
      } catch (e) {
        if (
          e.response?.status === NO_PERMISSION_FAILED_CODE ||
          e.xhr?.status === NO_PERMISSION_FAILED_CODE
        ) {
          setShowNoPermissionModal(true);
        } else {
          setShowRedirectModal(true);
        }
      }
    } else {
      try {
        await addTestCaseIssueTypeToProjectScheme(project.id);
      } catch (e) {
        if (
          e.response?.status === NO_PERMISSION_FAILED_CODE ||
          e.xhr?.status === NO_PERMISSION_FAILED_CODE
        ) {
          setShowNoPermissionModal(true);
        } else {
          StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
            e,
            messages['qalitySettings.error.addTestCaseIssueTypeToProjectScheme']
          );
        }
      }
    }
  };

  const subTitle = IS_CLOUD
    ? project.isTeamManaged()
      ? 'Team-managed'
      : 'Company-managed'
    : null;

  return (
    <Table.Tr isHoverable data-testid='QAlitySettingsTable.Row-Tr'>
      <Table.Td>
        <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
          <Issue
            iconSrc={project.getIconUrl()}
            title={project.name}
            subTitle={subTitle}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td>
        <Table.ContentHelper>
          <Toggle
            testId={`toggle_enable_plugin_${rowIndex}`}
            isChecked={project.isPluginEnabled}
            size='large'
            onChange={() => togglePluginInProject(project, rowIndex)}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td>
        <Table.ContentHelper muted={!project.isPluginEnabled}>
          <Toggle
            testId={`toggle_app_visibility_${rowIndex}`}
            isChecked={project.isAppVisibleInAllIssues}
            isDisabled={!project.isPluginEnabled}
            size='large'
            onChange={() => toggleAppVisibilityInAllIssues(project, rowIndex)}
          />
        </Table.ContentHelper>
      </Table.Td>

      <Table.Td>
        <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
          <StatusOfQAlityIssueTypeSetup
            project={project}
            toggleQalityTestIssueType={toggleQalityTestIssueType}
          />
        </Table.ContentHelper>
      </Table.Td>

      <RedirectToProjectSettingsConfirmationModal
        project={project}
        showModal={showRedirectModal}
        onClose={() => setShowRedirectModal(false)}
      />
      <NoPermissionToAddIssueTypeModal
        showModal={showNoPermissionModal}
        onClose={() => setShowNoPermissionModal(false)}
      />
    </Table.Tr>
  );
};
